import { ChangeEvent, FC, ReactNode, useEffect, useId, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { Icon } from '@components/icons';
import { joiningArrayWords, prettifyFileSize } from '@utils/index';
import Typography from '@components/common/typography';

export const fileFormatString = {
    '.doc': 'doc',
    '.xls': 'xls',
    'image/png': 'png',
    'image/jpg': 'jpg',
    'image/jpeg': 'jpeg',
    '.pdf': 'pdf',
    'application/pdf': 'pdf',
    '.docx': 'docx',
    '.csv': 'CSV',
    'text/csv': 'CSV'
};
export type TAcceptedFile =
    | '.doc'
    | '.xls'
    | '.csv'
    | 'image/png'
    | 'image/jpg'
    | 'image/jpeg'
    | '.pdf'
    | '.docx';

interface IProps {
    variant?: 'clickable' | 'default';
    label?: ReactNode;
    full?: boolean;
    size: number; // in MB
    accept: TAcceptedFile[];
    onChange: (files: FormData[]) => void;
    isHideDescription?: boolean;
    isOnlyFile?: boolean;
    onProcessFileInfo?: (file: FormData | File) => Promise<string>;
}

const cx = classNames.bind(styles);

interface FileInfo {
    name: string;
    id: number;
    size: number;
    type: TAcceptedFile;
    [key: string]: any;
}

const Uploader: FC<IProps> = ({
    variant = 'default',
    label,
    full,
    size,
    accept,
    onChange,
    isHideDescription,
    isOnlyFile,
    onProcessFileInfo
}) => {
    const [value, setValue] = useState<FormData[]>([]);
    const [filesInfo, setFilesInfo] = useState<FileInfo[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const id = useId();

    const processFiles = async (files: FileList) => {
        setValue([]);
        setFilesInfo([]);

        const uploadedFilesInfo: FileInfo[] = [];
        for (const item of Array.from(files)) {
            const successDescription = onProcessFileInfo && (await onProcessFileInfo(item));
            uploadedFilesInfo.push({
                id: Math.random(),
                name: item.name,
                size: item.size,
                type: item.type as TAcceptedFile,
                successDescription
            });

            const formData = new FormData();
            formData.append('file', item);
            setValue((state) => [...state, formData]);
        }
        console.log({ uploadedFilesInfo });

        setFilesInfo(uploadedFilesInfo);
    };

    const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const files = e?.target?.files;
        if (files) {
            processFiles(files);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    };

    useEffect(() => {
        onChange(value);
    }, [value]);

    return (
        <div className={cx(styles.wrapper, { full })}>
            {label}
            <label
                className={cx(styles.drop_wrapper, { [styles.drag_over]: isDragOver })}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>
                <input
                    type="file"
                    size={(size * 10) ^ 6}
                    accept={accept.join(',')}
                    onChange={handleUpload}
                    id={id}
                    style={{ display: 'none' }}
                    multiple
                />
                <Icon className={styles.icon} name="uploader" />
                <div className={styles.content_wrapper}>
                    <div className={styles.title}>
                        Drag and drop {isOnlyFile ? `a ${fileFormatString[accept?.[0]]}` : ''} file{' '}
                        or <span>upload</span>
                    </div>
                    {!isHideDescription && (
                        <div className={styles.accept}>
                            Accepted file types:{' '}
                            {joiningArrayWords(accept.map((item) => fileFormatString[item]))}. Max
                            file size:
                            {size}MB
                        </div>
                    )}
                </div>
            </label>
            {filesInfo.length > 0 && (
                <div className={styles.files_wrapper}>
                    {filesInfo.map((file, i) => {
                        console.log({ file });
                        return (
                            <div key={file.id} className={styles.file}>
                                <div className={styles.file_icon}>
                                    <Icon name="uploaded_file_format" width={24} height={32} />
                                    <p className={styles.text}>{fileFormatString[file.type]}</p>
                                </div>
                                <div className={styles.content}>
                                    <div className={styles.title}>
                                        <p>{file.name}</p>{' '}
                                        <span>{prettifyFileSize(file.size)}</span>
                                    </div>
                                    {file?.successDescription && (
                                        <Typography
                                            variant="caption1"
                                            className={styles.success_file_description}>
                                            {file?.successDescription}
                                        </Typography>
                                    )}
                                </div>
                                <button
                                    className={styles.delete}
                                    onClick={() => {
                                        setValue((state) =>
                                            state.filter((item, index) => i !== index)
                                        );
                                        setFilesInfo((state) =>
                                            state.filter((item, index) => i !== index)
                                        );
                                    }}>
                                    <Icon name="trash" width={18} height={18} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Uploader;
